import React, { useContext, useEffect, useRef, useState } from 'react'
import { View, Text, Pressable, Platform, TouchableOpacity, Share, Linking } from 'react-native'
import { Company, dimsScaledToHeight, Indicator, PopulatedValue, logoUrl } from '@ecountabl/lib'
import { useAppSelector } from '../store'
import { extra, MAX_CONTENT_WIDTH } from '../config'
import { textStyles } from '../style'
import DynamicImage from '../utility/dynamic-image'
import { ThemeContext } from '../utility/context/ThemeContext'
import { usePopover, showPopoverFromRef } from '../utility/popover'
import RNWebView from 'react-native-webview'
import { CustomActionSheet, CustomActionSheetRef } from '../utility/CustomActionSheet'
import Analytics from '../utility/analytics'
import Haptics from '../utility/haptics'
import { MaterialIcons } from '@expo/vector-icons'

type Props = {
  company: Company
  values: PopulatedValue[]
}

export const Certifications = ({ company, values }: Props): React.ReactElement => {
  const [certs, setCerts] = useState([])
  const datasources = useAppSelector((state) => state.datasources)

  const { theme } = useContext(ThemeContext)
  const { textMain, dark, danger } = theme.colors
  const { showFromAnchor, hide: hidePopover } = usePopover()
  const logoRefs = React.useRef<(View | null)[]>([])
  const openTimersRef = React.useRef<{ [k: number]: number | null }>({})
  const webviewSheetRef = useRef<CustomActionSheetRef>(null)
  const [selectedWebsite, setSelectedWebsite] = useState<string | null>(null)

  useEffect(() => {
    const _certs = []
    const isCert = (i: Indicator) => (
      i.certification_value &&
      company.calculatedRatings[i.abbreviation] == i.certification_value
    )

    values.forEach(v => {
      _certs.push(...v.indicators.filter(isCert))
    })

    setCerts(_certs)
  }, [company, values])

  const height = 50

  return certs.length ? (
    <View>
      <Text
        style={[
          textStyles.h3,
          { marginBottom: 8, color: textMain },
        ]}
      >
        Certifications and Commitments
      </Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-start', gap: 5 }}>
        {certs.map((c, i) => {
          const ds = datasources.map?.[c.indicator_family]
          const src = ds ? logoUrl(ds.s3ImgUrl, extra.cdn) : undefined
          if (!ds || !src) return null
          const name = ds.label || c.indicator_family
          const description = ds.description || ''
          let datasourceWebsite: URL
          if (ds.website) {
            try {
              datasourceWebsite = new URL(ds.website)
            } catch (e) {
              datasourceWebsite = new URL(`https://${ds.website}`)
            }
          }

          const content = (
            <View style={{ gap: 5, maxWidth: MAX_CONTENT_WIDTH / 2}}>
              <Text style={[textStyles.h4, { fontWeight: 'bold', color: textMain }]}>{name}</Text>
              {description ? (
                <Text style={[textStyles.label, { color: textMain }]}>
                  {description}
                </Text>
              ) : null}
              {datasourceWebsite ? (
                <TouchableOpacity onPress={() => {
                  Haptics.selection()
                  Analytics.track('certification_website_clicked', {
                    companyId: company._id,
                    companyName: company.name,
                    certificationKey: c.indicator_family,
                    certificationName: name,
                    website: datasourceWebsite.href
                  })
                  hidePopover()
                  if (Platform.OS === 'web') {
                    // on web, open in a new tab
                    Linking.openURL(datasourceWebsite.href)
                  } else {
                    setSelectedWebsite(datasourceWebsite.href)
                    webviewSheetRef.current?.show()
                  }
                }}>
                  <Text
                    style={[textStyles.label, { textDecorationLine: 'underline', color: textMain }]}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {datasourceWebsite.href}
                  </Text>
                </TouchableOpacity>
              ) : null}
            </View>
          )
          const openPopover = () => {
            showPopoverFromRef(
              { current: logoRefs.current[i] },
              showFromAnchor,
              content,
              undefined,
              {
                offset: { x: 0, y: 0 },
                align: 'auto',
                showArrow: true
              }
            )
          }
          const scheduleOpen = () => {
            if (openTimersRef.current[i]) {
              clearTimeout(openTimersRef.current[i] as number)
            }
            openTimersRef.current[i] = setTimeout(openPopover, 150) as unknown as number
          }
          return (
            <Pressable
              key={i}
              ref={(el) => { logoRefs.current[i] = el }}
              onPress={Platform.OS !== 'web' ? () => {
                Haptics.selection()
                Analytics.track('certification_clicked', {
                  companyId: company._id,
                  companyName: company.name,
                  certificationKey: c.indicator_family,
                  certificationName: name,
                  index: i
                })
                openPopover()
              } : undefined}
              onHoverIn={Platform.OS === 'web' ? scheduleOpen : undefined}
              hitSlop={10}
              style={{ alignSelf: 'flex-start'}}
            >
              <DynamicImage
                src={src}
                alt={`${c.indicator_family} logo`}
                style={{
                  resizeMode: 'contain',
                  height,
                  width: Math.min(dimsScaledToHeight(50, c.logo_width, c.logo_height).width, 160)
                }}
                fallbackIcon={<MaterialIcons name="stars" size={28} color={dark}/>}
                errorIcon={<MaterialIcons name="error-outline" size={32} color={danger}/>}
              />
            </Pressable>
          )
        })}
      </View>
      {
        Platform.OS !== 'web' && selectedWebsite ? (
          <CustomActionSheet
            ref={webviewSheetRef}
            name="certification-website"
            menuContent={
              Linking.canOpenURL(selectedWebsite) ? (
                <View>
                  <TouchableOpacity onPress={() => {
                    Haptics.selection()
                    Linking.openURL(selectedWebsite)
                  }}>
                    <View style={{flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 10, marginVertical: 8}}>
                      <Text style={[textStyles.body, {color: textMain}]}>Open in Browser</Text>
                    </View>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => {
                    Haptics.selection()
                    Share.share({ url: selectedWebsite })
                  }}>
                    <View style={{flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 10, marginVertical: 8}}>
                      <Text style={[textStyles.body, {color: textMain}]}>Share</Text>
                    </View>
                  </TouchableOpacity>
                </View>
              ) : null
            }
          >
            <View style={{height: "100%"}}>
              <RNWebView source={{ uri: selectedWebsite }} style={{height: "100%", width: "100%"}}/>
            </View>
          </CustomActionSheet>
        ) : null
      }
    </View>
  ) : null
}

export default Certifications

